/**
 * Service: team-message.service.ts
 *
 * All Supabase access for the Koredor internal team messaging feature.
 * Covers: team_conversations, team_conversation_members, team_messages,
 *         team_message_reads, and org member profiles.
 *
 * org_id pattern: getCurrentOrganizationId() from @/helpers/auth.helpers
 * Reference implementation: src/services/lead.service.ts
 *
 * Do NOT call supabase directly from UI components — always go through this file.
 */

import { supabase } from '@/lib/supabase';
import { getCurrentOrganizationId } from '@/helpers/auth.helpers';
import {
  toUiConversation,
  toUiConversationMember,
  toUiMessage,
  toUiOrgMember,
} from '@/mappers/team-message.mapper';
import type {
  TeamConversation,
  TeamConversationMember,
  TeamMessage,
  TeamMessageAttachment,
  OrgMember,
} from '@/types/teamMessages';

// =============================================================================
// Internal helpers
// =============================================================================

async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

// =============================================================================
// Conversations
// =============================================================================

/**
 * Returns all team_conversations that the current user is a member of,
 * ordered by most recently updated first.
 * Members are hydrated inline via a joined select; last message and unread
 * counts are kept as zero/null here — the hook layer enriches them if needed.
 */
export async function getMyConversations(): Promise<TeamConversation[]> {
  const orgId = await getCurrentOrganizationId();
  const user = await getCurrentUser();
  if (!user || !orgId) return [];

  // Fetch conversations with member user_ids only (no profiles join — member
  // user_id FKs to auth.users which PostgREST cannot traverse to profiles).
  // No org filter here — RLS (tc_select) handles scoping, and super_admin
  // users need to see updates channels from all orgs via the bypass policy.
  const { data, error } = await supabase
    .from('team_conversations')
    .select(`
      id,
      organization_id,
      type,
      name,
      created_by,
      created_at,
      updated_at,
      team_conversation_members ( user_id, joined_at )
    `)
    .order('updated_at', { ascending: false });

  if (error) throw error;

  const rows = data ?? [];

  // If multiple updates channels are visible (super_admin bypass), suppress
  // the placeholder org's empty channel in favour of the real org's channel.
  const PLACEHOLDER_ORG = '00000000-0000-0000-0000-000000000001';
  const hasRealUpdatesChannel = rows.some(
    (r) => (r as any).type === 'updates' && (r as any).organization_id !== PLACEHOLDER_ORG,
  );
  const filteredRows = hasRealUpdatesChannel
    ? rows.filter(
        (r) => !((r as any).type === 'updates' && (r as any).organization_id === PLACEHOLDER_ORG),
      )
    : rows;

  // Collect all unique member auth-UIDs across all conversations so we can
  // resolve display names in one round-trip. DM names depend on this because
  // the DM partner may not be in the caller's org (orgMembers is org-scoped).
  const allUserIds = [
    ...new Set(
      filteredRows.flatMap((row) =>
        ((row.team_conversation_members ?? []) as Array<{ user_id: string }>).map((m) => m.user_id),
      ),
    ),
  ];

  // Build auth-UID → display name map.
  // Primary: profiles.auth_user_id lookup (most reliable).
  // Secondary: profiles.id lookup (covers profiles where auth_user_id = id).
  // Tertiary: get_dm_partner_names() SECURITY DEFINER RPC which can read
  //           auth.users directly — catches any remaining gaps.
  const nameMap = new Map<string, string>();
  if (allUserIds.length > 0) {
    const { data: byAuthId } = await supabase
      .from('profiles')
      .select('auth_user_id, full_name, email')
      .in('auth_user_id', allUserIds);
    for (const p of (byAuthId ?? []) as Array<Record<string, unknown>>) {
      const name = String(p.full_name ?? p.email ?? 'Unknown');
      nameMap.set(String(p.auth_user_id), name);
    }
    const unfound = allUserIds.filter((id) => !nameMap.has(id));
    if (unfound.length > 0) {
      const { data: byId } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', unfound);
      for (const p of (byId ?? []) as Array<Record<string, unknown>>) {
        const name = String(p.full_name ?? p.email ?? 'Unknown');
        nameMap.set(String(p.id), name);
      }
    }
  }

  // Tertiary: SECURITY DEFINER RPC that reads auth.users directly.
  // Builds TWO maps:
  //   nameMap (user_id → name) — fills gaps in per-member displayName
  //   dmNameByConvId (conv_id → partnerName) — keyed by conversation so we
  //     never need a userId match at render time.
  const dmNameByConvId = new Map<string, string>();
  const { data: dmPartners } = await supabase.rpc('get_dm_partner_names');
  for (const row of (dmPartners ?? []) as Array<{ conversation_id: string; partner_user_id: string; display_name: string }>) {
    if (row.display_name) {
      dmNameByConvId.set(row.conversation_id, row.display_name);
      if (!nameMap.has(row.partner_user_id)) {
        nameMap.set(row.partner_user_id, row.display_name);
      }
    }
  }

  function toInitials(name: string) {
    return name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  }

  return filteredRows.map((row) => {
    const rawMembers = (row.team_conversation_members ?? []) as Array<{ user_id: string; joined_at: string }>;
    const members: TeamConversationMember[] = rawMembers.map((m) => {
      const name = nameMap.get(m.user_id) ?? '';
      return {
        userId: m.user_id,
        displayName: name,
        initials: name ? toInitials(name) : '',
        role: '',
        joinedAt: m.joined_at,
      } satisfies TeamConversationMember;
    });

    // For DM conversations, stamp the resolved partner name into conv.name
    // so ConversationListPanel and ThreadPanel can use it directly without
    // needing a runtime userId-matching lookup.
    const rowType = String((row as any).type ?? '');
    const dmName = rowType === 'dm' ? (dmNameByConvId.get(String((row as any).id)) ?? null) : null;
    const rowWithName = dmName
      ? { ...(row as Record<string, unknown>), name: dmName }
      : (row as Record<string, unknown>);

    return toUiConversation(rowWithName, members, null, 0);
  });
}

/**
 * Creates a new group conversation and adds all specified members plus the
 * current user. Uses a SECURITY DEFINER RPC to avoid the PostgREST
 * return=representation RLS re-check that fires before the creator is
 * added to team_conversation_members.
 */
export async function createGroupConversation(
  name: string,
  memberIds: string[],
): Promise<TeamConversation> {
  const orgId = await getCurrentOrganizationId();
  const user = await getCurrentUser();
  if (!user || !orgId) throw new Error('Not authenticated');

  const { data: convId, error } = await supabase.rpc('create_team_group_conversation', {
    p_org_id: orgId,
    p_name: name,
    p_member_ids: memberIds,
  });

  if (error) throw error;

  return toUiConversation(
    {
      id: convId as string,
      organization_id: orgId,
      type: 'group',
      name,
      created_by: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    [],
    null,
    0,
  );
}

/**
 * Finds an existing DM between the current user and `otherUserId`, or creates
 * one if none exists. Uses a SECURITY DEFINER RPC for creation to avoid the
 * PostgREST return=representation RLS re-check issue.
 */
export async function findOrCreateDM(otherUserId: string): Promise<TeamConversation> {
  const orgId = await getCurrentOrganizationId();
  const user = await getCurrentUser();
  if (!user || !orgId) throw new Error('Not authenticated');

  const { data: convId, error } = await supabase.rpc('find_or_create_team_dm', {
    p_org_id: orgId,
    p_other_user_id: otherUserId,
  });

  if (error) throw error;

  return toUiConversation(
    {
      id: convId as string,
      organization_id: orgId,
      type: 'dm',
      name: null,
      created_by: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    [],
    null,
    0,
  );
}

/**
 * Finds or creates the org-wide "general" channel and ensures the current
 * user is a member.  Returns the conversation UUID.  Safe for concurrent calls.
 */
export async function findOrCreateGeneralChannel(): Promise<string> {
  const orgId = await getCurrentOrganizationId();
  if (!orgId) throw new Error('Not authenticated');

  const { data: convId, error } = await supabase.rpc('find_or_create_general_channel', {
    p_org_id: orgId,
  });

  if (error) throw error;
  return convId as string;
}

/**
 * Finds or creates the org-wide "updates" channel and ensures the current
 * user is a member.  Returns the conversation UUID.  Safe for concurrent calls.
 */
export async function findOrCreateUpdatesChannel(): Promise<string> {
  const orgId = await getCurrentOrganizationId();
  if (!orgId) throw new Error('Not authenticated');

  const { data: convId, error } = await supabase.rpc('find_or_create_updates_channel', {
    p_org_id: orgId,
  });

  if (error) throw error;
  return convId as string;
}

/**
 * Creates a new channel (org-wide named room). Uses a SECURITY DEFINER RPC
 * for the same reason as createGroupConversation — avoids the RETURNING
 * RLS re-check before the creator is in team_conversation_members.
 */
export async function createChannel(name: string): Promise<TeamConversation> {
  const orgId = await getCurrentOrganizationId();
  const user = await getCurrentUser();
  if (!user || !orgId) throw new Error('Not authenticated');

  const { data: convId, error } = await supabase.rpc('create_team_channel', {
    p_org_id: orgId,
    p_name: name,
  });

  if (error) throw error;

  return toUiConversation(
    {
      id: convId as string,
      organization_id: orgId,
      type: 'channel',
      name,
      created_by: user.id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    [],
    null,
    0,
  );
}

/**
 * Adds one or more org members to an existing group conversation.
 * Uses the SECURITY DEFINER RPC to bypass RLS on team_conversation_members.
 * Throws on any error.
 */
export async function addMembersToConversation(
  conversationId: string,
  memberIds: string[],
): Promise<void> {
  const { error } = await supabase.rpc('add_members_to_conversation', {
    p_conversation_id: conversationId,
    p_member_ids: memberIds,
  });
  if (error) throw error;
}

// =============================================================================
// Messages
// =============================================================================

/**
 * Loads messages for a conversation, most recent `limit` messages, ordered
 * ascending (oldest first) for display. Pass `before` (ISO timestamp) to
 * paginate backwards.
 *
 * NOTE: We do NOT join profiles here because team_messages.sender_id FKs to
 * auth.users (cross-schema), which PostgREST cannot traverse for embedded
 * resource joins. Instead we do a separate profiles lookup after fetching rows.
 */
export async function getMessages(
  conversationId: string,
  limit = 50,
  before?: string,
): Promise<TeamMessage[]> {
  const user = await getCurrentUser();
  if (!user) return [];

  let query = supabase
    .from('team_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []).reverse();

  // Resolve sender display names. sender_id is the auth UUID; profiles uses
  // auth_user_id for that mapping (dual-column pattern used across the codebase).
  // Filter out null sender_ids (system messages) before querying profiles.
  const senderIds = [
    ...new Set(
      rows
        .filter((r) => (r as Record<string, unknown>).sender_id != null)
        .map((r) => String((r as Record<string, unknown>).sender_id))
    ),
  ];
  const nameMap = new Map<string, string>();

  if (senderIds.length > 0) {
    const { data: byAuthId } = await supabase
      .from('profiles')
      .select('auth_user_id, full_name, email')
      .in('auth_user_id', senderIds);

    for (const p of (byAuthId ?? []) as Array<Record<string, unknown>>) {
      const name = String(p.full_name ?? p.email ?? 'Unknown');
      nameMap.set(String(p.auth_user_id), name);
    }

    // Fallback: some deployments store auth UID directly in profiles.id
    const unfound = senderIds.filter((id) => !nameMap.has(id));
    if (unfound.length > 0) {
      const { data: byId } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', unfound);
      for (const p of (byId ?? []) as Array<Record<string, unknown>>) {
        const name = String(p.full_name ?? p.email ?? 'Unknown');
        nameMap.set(String(p.id), name);
      }
    }
  }

  return rows.map((row) => {
    const senderId = String((row as Record<string, unknown>).sender_id);
    const senderName = nameMap.get(senderId) ?? 'Unknown';
    return toUiMessage(row as Record<string, unknown>, user.id, senderName);
  });
}

/**
 * Sends a message to a conversation. The sender_id is always the authenticated
 * user — this is enforced both here and by the tm_insert RLS policy.
 * Updates the conversation's updated_at so it rises to the top of the list.
 */
export async function sendMessage(
  conversationId: string,
  body: string,
  attachments: TeamMessageAttachment[] = [],
  /** Pass the caller's display name to skip an extra profiles round-trip. */
  knownSenderName?: string,
): Promise<TeamMessage> {
  const user = await getCurrentUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('team_messages')
    .insert({
      conversation_id: conversationId,
      sender_id: user.id,
      body,
      attachments,
    })
    .select('*')
    .single();

  if (error) throw error;

  // Bump updated_at on the conversation — fire-and-forget.
  void supabase
    .from('team_conversations')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', conversationId);

  // Only hit profiles if the caller didn't already know the name.
  let senderName = knownSenderName ?? 'Unknown';
  if (!knownSenderName) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, email')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    if (profile) {
      senderName = String(
        (profile as Record<string, unknown>).full_name ??
        (profile as Record<string, unknown>).email ??
        'Unknown',
      );
    }
  }

  return toUiMessage(data as Record<string, unknown>, user.id, senderName);
}

/**
 * Marks all unread messages in a conversation as read for the current user.
 * Uses a subquery approach to avoid fetching the full read list client-side.
 */
export async function markMessagesRead(conversationId: string): Promise<void> {
  const user = await getCurrentUser();
  if (!user) return;

  // Fetch unread message IDs for this conversation.
  // Include system messages (sender_id IS NULL) via .or() so they count as unread.
  const { data: allMessages } = await supabase
    .from('team_messages')
    .select('id')
    .eq('conversation_id', conversationId)
    .or(`sender_id.is.null,sender_id.neq.${user.id}`);

  if (!allMessages?.length) return;

  const { data: alreadyRead } = await supabase
    .from('team_message_reads')
    .select('message_id')
    .eq('user_id', user.id)
    .in('message_id', allMessages.map((m) => m.id));

  const readSet = new Set((alreadyRead ?? []).map((r) => r.message_id));
  const unreadIds = allMessages.filter((m) => !readSet.has(m.id)).map((m) => m.id);

  if (!unreadIds.length) return;

  await supabase
    .from('team_message_reads')
    .insert(unreadIds.map((message_id) => ({ message_id, user_id: user.id })));
}

/**
 * Returns the total count of unread messages across all conversations the
 * current user belongs to. Used for the nav badge.
 */
export async function getTotalUnreadCount(): Promise<number> {
  const user = await getCurrentUser();
  if (!user) return 0;

  // Use all RLS-visible conversations (not just memberships) so super_admin
  // users see unread counts from updates channels they access via bypass policy.
  const { data: convs } = await supabase
    .from('team_conversations')
    .select('id');

  const convIds = (convs ?? []).map((c) => c.id);
  if (!convIds.length) return 0;

  // Include system messages (sender_id IS NULL) so updates channel notifications fire.
  const [{ data: allMessages }, { data: reads }] = await Promise.all([
    supabase
      .from('team_messages')
      .select('id')
      .in('conversation_id', convIds)
      .or(`sender_id.is.null,sender_id.neq.${user.id}`),
    supabase
      .from('team_message_reads')
      .select('message_id')
      .eq('user_id', user.id),
  ]);

  if (!allMessages?.length) return 0;
  const readIds = new Set((reads ?? []).map((r) => r.message_id));
  return allMessages.filter((m) => !readIds.has(m.id)).length;
}

/**
 * Returns a map of conversationId → most recent TeamMessage (preview only —
 * senderName is not resolved to keep the query fast). Used to populate the
 * conversation list after loadConversations().
 */
export async function getLastMessagesPerConversation(
  conversationIds: string[],
): Promise<Map<string, TeamMessage>> {
  const user = await getCurrentUser();
  if (!user || !conversationIds.length) return new Map();

  const { data } = await supabase
    .from('team_messages')
    .select('id, conversation_id, sender_id, body, created_at, is_system, attachments, metadata')
    .in('conversation_id', conversationIds)
    .order('created_at', { ascending: false });

  const map = new Map<string, TeamMessage>();
  for (const row of (data ?? []) as Array<Record<string, unknown>>) {
    const convId = String(row.conversation_id);
    if (!map.has(convId)) {
      // Minimal preview — senderName left blank (not needed for list preview)
      map.set(convId, toUiMessage(row, user.id, ''));
    }
  }
  return map;
}

/**
 * Returns a map of conversationId → unread message count for the given
 * conversation IDs. Includes system messages (sender_id IS NULL).
 */
export async function getUnreadCountsPerConversation(
  conversationIds: string[],
): Promise<Map<string, number>> {
  const user = await getCurrentUser();
  if (!user || !conversationIds.length) return new Map();

  const [{ data: allMessages }, { data: reads }] = await Promise.all([
    supabase
      .from('team_messages')
      .select('id, conversation_id')
      .in('conversation_id', conversationIds)
      .or(`sender_id.is.null,sender_id.neq.${user.id}`),
    supabase
      .from('team_message_reads')
      .select('message_id')
      .eq('user_id', user.id),
  ]);

  const readIds = new Set((reads ?? []).map((r) => r.message_id));
  const countMap = new Map<string, number>();
  for (const msg of (allMessages ?? []) as Array<{ id: string; conversation_id: string }>) {
    if (!readIds.has(msg.id)) {
      countMap.set(msg.conversation_id, (countMap.get(msg.conversation_id) ?? 0) + 1);
    }
  }
  return countMap;
}

// =============================================================================
// Mentions
// =============================================================================

/**
 * Inserts mention rows into team_message_mentions after a message is sent.
 * Called client-side immediately after sendMessage() resolves.
 * Silently no-ops if mentionedUserIds is empty.
 */
export async function insertMentions(
  messageId: string,
  conversationId: string,
  mentionedUserIds: string[],
): Promise<void> {
  if (!mentionedUserIds.length) return;
  const orgId = await getCurrentOrganizationId();
  if (!orgId) return;

  const rows = mentionedUserIds.map((uid) => ({
    message_id: messageId,
    conversation_id: conversationId,
    mentioned_user_id: uid,
    organization_id: orgId,
  }));

  // Ignore duplicate errors (onConflict: ignore) in case of retries
  await supabase.from('team_message_mentions').insert(rows);
}

/**
 * Returns the total count of unread mention rows for the current user.
 */
export async function getUnreadMentionCount(): Promise<number> {
  const user = await getCurrentUser();
  if (!user) return 0;

  const { count } = await supabase
    .from('team_message_mentions')
    .select('id', { count: 'exact', head: true })
    .eq('mentioned_user_id', user.id)
    .eq('is_read', false);

  return count ?? 0;
}

/**
 * Marks all unread mention rows for the current user in a given conversation as read.
 * Called when the user opens a conversation.
 */
export async function markMentionsRead(conversationId: string): Promise<void> {
  const user = await getCurrentUser();
  if (!user) return;

  await supabase
    .from('team_message_mentions')
    .update({ is_read: true })
    .eq('mentioned_user_id', user.id)
    .eq('conversation_id', conversationId)
    .eq('is_read', false);
}

// =============================================================================
// Org members (for @ mentions and new conversation member picker)
// =============================================================================

/**
 * Returns all profiles within the current user's organization.
 * Used to populate the @ mention picker and new group/DM member selector.
 */
export async function getOrgMembers(): Promise<OrgMember[]> {
  const orgId = await getCurrentOrganizationId();
  if (!orgId) return [];

  const { data, error } = await supabase
    .from('profiles')
    .select('id, auth_user_id, full_name, email, role')
    .eq('organization_id', orgId)
    .order('full_name');

  if (error) throw error;

  return (data ?? []).map((row) => toUiOrgMember(row as Record<string, unknown>));
}
